import { Inject, Injectable, InjectionToken } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import { catchError, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as FlatModels from '@medsurf/flat-models';
import { AlertControlActions, AuthControlActions, HistoryControlActions, MediaEntityActions } from '@medsurf/flat-actions';
import { Router } from "@angular/router";
import * as tus from 'tus-js-client';

export const FLAT_SETTINGS = new InjectionToken<SettingControlModel>('flat_settings');

export interface SettingControlModel {
  s3Port?: string;
  s3Host?: string;
  s3endpointUrl?: string;
  s3Bucket?: string;
  s3Secure?: boolean;
  endpointUrl?: string;
  endpointUrlPrint?: string;
  authorUrl?: string;
  viewerUrl?: string;
  version?: string;
  timestamp?: string;
  revision?: string;
  sentryDsn?: string;
  sentryEnvironment?: string;
  deepzoomProject?: string;
  deepzoomStatsUrl?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ApiControlService {
  #sliceSize = 10 * 1024 * 1024; // ~10MB

  constructor(private store: Store,
              private http: HttpClient,
              private router: Router,
              @Inject(FLAT_SETTINGS) private settings: SettingControlModel) {
  }

  //<editor-fold desc="Request">

  sendMessage(data: any) {
    // Ensure type is present
    data = Object.assign({type: data.constructor.type}, data);

    // Execute Request
    return this.http.post<unknown>(
      this.settings.endpointUrl + 'v2/httpws', 
      data, 
      { 
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        withCredentials: true
      }).pipe(
      catchError(result => {
        if (result.error instanceof ProgressEvent) {
          this.store.dispatch(new AlertControlActions.AddAlert({
            key: 'main',
            detail: 'notification.unknown_api_error',
            severity: FlatModels.AlertControlModels.AlertType.enum.error,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          }));
        } else if (FlatModels.ErrorControlModels.MedsurfErrorNames.includes(result.error.name)) {
          // Cast Error
          const error = result.error as FlatModels.ErrorControlModels.MedsurfError;

          // Show Error
          if (error.options?.show) {
            this.store.dispatch(new AlertControlActions.AddAlert({
              key: 'main',
              id: error.uuid,
              detail: error.message,
              severity: FlatModels.AlertControlModels.AlertType.enum.error,
              life: FlatModels.AlertControlModels.AlertTime.DEFAULT
            }));
          }

          if (error.options?.redirectToLogin) {
            if (error.options?.allowedRedirectUrl) {
              this.store.dispatch(new Navigate([error.options?.allowedRedirectUrl]));
            } else {
              this.store.dispatch(new AuthControlActions.ClearSession());
            }
          }

          // Redirect To 404
          if (error.options?.redirectToErrorPage) {
            this.store.dispatch(new Navigate(['404']));
          }
        }

        // Return error
        return throwError(() => {
          return result;
        });
      }),
      tap((result: any) => {
        if (result.name !== FlatModels.ErrorControlModels.MedsurfError.name) {
          if (result.type) {
            this.store.dispatch(result);
          }
        }
      }),
    );
  }

  //</editor-fold>

  //<editor-fold desc="Upload">
  
  async createMediaUpload(options: {
    file: File,
    action: FlatModels.ApiControlModels.UploadMediaMetadata['action'],
    mediaId?: string,
    accept?: string,
    maxSize?: number,
    blockUi?: boolean,
    onError?: (error: Error) => void, 
    onProgress?: (progress: number) => void
    onSuccess?: (data: FlatModels.MediaEntityModels.MediaEntityType) => void
  }) {
    const metadata: FlatModels.ApiControlModels.UploadMediaMetadata = {
      action: options.action,
      filename: options.file.name,
      filetype: options.file.type,
      ...(options.mediaId ? { mediaId: options.mediaId } : {}),
    }

    return this.createUpload({
      file: options.file,
      metadata,
      accept: options.accept,
      maxSize: options.maxSize,
      blockUi: options.blockUi,
      onError: options.onError,
      onProgress: options.onProgress,
      onSuccess: (response) => {
        if (!response.data?.id) throw new Error('No Media received');
        const media = response.data as FlatModels.MediaEntityModels.MediaEntityType;

        this.store.dispatch(new MediaEntityActions.ModifyEntitySuccess({
          id: null,
          snapshotId: null,
          stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_ENTITY,
          modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
          entity: media,
        }));

        options.onSuccess?.(media);
      }
    });
  }

  async createUpload(options: {
    file: File,
    metadata: Record<string, string>,
    blockUi?: boolean,
    accept?: string,
    maxSize?: number,
    onError?: (error: Error) => void, 
    onProgress?: (progress: number) => void 
    onSuccess?: (data: any) => void
  }) {
    if (options.blockUi) this.store.dispatch(new HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.uploading));

    const errorHandler = (error: Error) => {
      this.store.dispatch(new AlertControlActions.AddAlert({
        key: 'main',
        detail: error.message,
        severity: FlatModels.AlertControlModels.AlertType.enum.error,
        life: FlatModels.AlertControlModels.AlertTime.DEFAULT
      }));
      if (options.blockUi) this.store.dispatch(new HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));

      options.onError?.(error)
    }

    const upload = new tus.Upload(options.file, {
      endpoint: `${this.settings.endpointUrl}v2/uploads/`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: options.metadata,
      chunkSize: 1024 * 1024 * 10, // 10 MB
      addRequestId: true,
      onBeforeRequest: (req) => {
        const xhr: XMLHttpRequest = req.getUnderlyingObject()
        xhr.withCredentials = true
      },
      onError: async (error) => {
        let errorMessage = error.message;

        if (error instanceof tus.DetailedError) {
          const body = error.originalResponse?.getBody()
          const parsedBody = body ? JSON.parse(body) : {};
          if (parsedBody.error) {
            errorMessage = parsedBody.error;
          }
        }

        errorHandler(new Error(errorMessage))
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Number(((bytesUploaded / bytesTotal) * 100).toFixed(2))
        options.onProgress?.(percentage)
      },
      onSuccess: (payload) => {
        const body = payload.lastResponse.getBody();
        const parsedBody = JSON.parse(body);
        if (options.blockUi) this.store.dispatch(new HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        options.onSuccess?.(parsedBody);
      },
    })

    if (options.accept) {
      const acceptRegExp = options.accept.split(',').map(accept => new RegExp('^' + accept.replace('*', '.*') + '$'));
      if (!acceptRegExp.some(acceptRegExp => acceptRegExp.test(options.file.type))) {
        errorHandler(new Error('notification.file_upload_mime_not_supported'));
        upload.abort(true)
        return upload;
      }
    }

    if (options.maxSize) {
      if (options.file.size > options.maxSize) {
        errorHandler(new Error('notification.file_upload_size_error'));
        upload.abort(true)
        return upload;
      }
    }
  
    const previousUploads = await upload.findPreviousUploads()
    if (previousUploads.length) {
      upload.resumeFromPreviousUpload(previousUploads[0])
    }

    upload.start()
    return upload;
  }

  //</editor-fold>
}
